import {
    ChangeEvent, useCallback, useMemo,
} from 'react'
import Pagination, {
    PaginationRenderItemParams,
} from '@mui/material/Pagination'
import Card from '@mui/material/Card'
import PaginationItem from '@mui/material/PaginationItem'
import useStyles from './SkyNetPagination.style'

export enum SkyNetPaginationMode {
    SIMPLE,
    NUMERICAL
}

type Props = {
    total?: number,
    rowsPerPage?: number,
    mode?: SkyNetPaginationMode,
    page?: number,
    onChange: (e: ChangeEvent, page: number) => void,
    disabled?: boolean,
    useCard?: boolean,
    showAlways?: boolean,
}

const defaultProps: Partial<Props> = {
    total: 0,
    rowsPerPage: 1,
    mode: SkyNetPaginationMode.NUMERICAL,
    page: 1,
    disabled: false,
    useCard: true,
    showAlways: false,
}

const SkyNetPagination = ({
    total,
    rowsPerPage,
    mode,
    page,
    onChange,
    disabled,
    useCard,
    showAlways,
}: Readonly<Props>) => {
    const {
        classes, cx,
    } = useStyles()

    const count = Number.parseInt(String(total / rowsPerPage), 10)
        + (total % rowsPerPage > 0 ? 1 : 0)

    const label = useMemo(() => {
        if (total === 0) {
            return '0 of 0'
        }

        const prefix = mode === SkyNetPaginationMode.NUMERICAL ? 'Records ' : ''

        const first = page * rowsPerPage - (rowsPerPage - 1)
        const last = (page * rowsPerPage) < total ? (page * rowsPerPage) : total

        if (first === last) return `${prefix}${first} of ${total}`
        return `${prefix}${first} - ${last} of ${total}`
    }, [
        mode,
        page,
        rowsPerPage,
        total,
    ])

    const renderItem = useCallback((item: PaginationRenderItemParams) => {
        if (mode === SkyNetPaginationMode.SIMPLE && ![
            'previous',
            'next',
        ].includes(item.type)) {
            return null
        }
        return (
            <PaginationItem
                {...item} // eslint-disable-line react/jsx-props-no-spreading
                className={classes.pgItem}
            />
        )
    }, [
        classes.pgItem,
        mode,
    ])
    const PaginationComponent = useMemo(() => {
        return (
            <>
                <span className={classes.label}>{label}</span>
                <Pagination
                    disabled={disabled}
                    count={count}
                    page={page}
                    onChange={onChange}
                    renderItem={renderItem}
                    inputMode="numeric"
                    size="small"
                    variant="outlined"
                    classes={{
                        ul: classes.pgUl,
                    }}
                />
            </>
        )
    }, [
        classes,
        count,
        disabled,
        label,
        onChange,
        page,
        renderItem,
    ])

    if (count <= 1 && !showAlways) {
        return null
    }

    if (!useCard) {
        return (
            <div
                className={classes.container}
                data-testid="pagination-no-card"
            >
                {PaginationComponent}
            </div>
        )
    }
    return (
        <Card
            className={cx(classes.container, classes.cardContainer)}
            data-testid="pagination-with-card"
        >
            {PaginationComponent}
        </Card>
    )
}

SkyNetPagination.defaultProps = defaultProps

export default SkyNetPagination
